import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/auth-context';

/**
 * Route guard. Reads the AuthContext and:
 *
 *   loading         → centered spinner
 *   unauthenticated → redirect to /login, preserving the attempted location in
 *                     route state so login-page can bounce back after success
 *   authenticated   → render children
 *
 * This component does NOT itself fetch /auth/me — that's the AuthProvider's
 * job at mount. It just reads.
 */
export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { status } = useAuth();
  const location = useLocation();
  const { t } = useTranslation('common');

  if (status === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={t('loading')}
        data-testid="auth-loading"
        className="flex min-h-screen items-center justify-center bg-background"
      >
        <div
          className="h-10 w-10 animate-spin rounded-full border-4 border-muted border-t-foreground"
          aria-hidden="true"
        />
        <span className="sr-only">{t('loading')}</span>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

export default RequireAuth;
